import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';

import confirmHardwareSettingsSelector from '../../../../selectors/testOverview/testComponents/hardwareAccessCheck/ConfirmHardwareSettingsSelector';
import liveSessioner from '../../../../services/livesession/LiveSessioner';
import livestreamActions from '../../../../actions/LivestreamActions';
import streamingStarterService from '../../../../services/livesession/StreamingStarterService';

import IconSelect from './IconSelect';
import LiveCamera from './LiveCamera';
import PlayTestSoundButtonWithAudio from './PlayTestSoundButtonWithAudio';

import styles from '../../testOverview.scss';

export class ConfirmHardwareSettings extends React.PureComponent {
    static propTypes = {
        audioDeviceId: PropTypes.string,
        audioDevices: PropTypes.array,
        dispatch: PropTypes.func.isRequired,
        hardwareCheckIsOffline: PropTypes.bool.isRequired,
        isConfirmHardwareSettingsButtonDisabled: PropTypes.bool,
        showSelectAudioDevice: PropTypes.bool.isRequired,
        showSelectSpeakerDevice: PropTypes.bool.isRequired,
        showSelectWebcamDevice: PropTypes.bool.isRequired,
        showWarningMissingAudioDevice: PropTypes.bool.isRequired,
        showWarningMissingWebcamDevice: PropTypes.bool.isRequired,
        speakerDeviceId: PropTypes.string,
        speakerDevices: PropTypes.array,
        videoDevices: PropTypes.array,
        webcamDeviceId: PropTypes.string,
        webcamStream: PropTypes.object,
    };

    componentWillUnmount() {
        streamingStarterService.cancelRecorderAlert();
    }

    handleConfirmSettings = () => {
        const { dispatch, hardwareCheckIsOffline, webcamDeviceId } = this.props;

        dispatch(livestreamActions.confirmHardwareSettings());

        if (!hardwareCheckIsOffline) {
            streamingStarterService.startStreaming(webcamDeviceId);
        }
    };

    handleSwitchWebcamDevice = (e) => {
        const { audioDeviceId } = this.props;
        const options = e.currentTarget.options;
        const selectedOption = Array.from(options).find((option) => {
            return option.selected;
        });
        const selectedDeviceId = selectedOption.value;
        liveSessioner.switchDevices(selectedDeviceId, audioDeviceId);
    };

    handleSwitchAudioDevice = (e) => {
        const { webcamDeviceId } = this.props;
        const options = e.currentTarget.options;
        const selectedOption = Array.from(options).find((option) => {
            return option.selected;
        });
        const selectedDeviceId = selectedOption.value;
        liveSessioner.switchDevices(webcamDeviceId, selectedDeviceId);
    };

    handleSwitchSpeakerDevice = (e) => {
        const { dispatch } = this.props;
        const selectedDeviceId = e.target.value;

        dispatch(livestreamActions.switchSpeakerDevice(selectedDeviceId));
    };

    /**
     * Look at the comment in setSpeakerDeviceId in LivestreamReducer to learn
     * more.
     */
    speakerSelectValue(deviceList, deviceId) {
        if (deviceList.length < 1 || deviceId) {
            return deviceId;
        }

        return deviceList[0].deviceId;
    }

    improveDeviceLabel(deviceId, label) {
        /**
         * RP-2016: Add Default / Communications prefix when Chromium's Inspera
         * Exam Portal doesn't add it.
         */
        if (
            label.indexOf(' - ') === 0
            && (deviceId === 'default' || deviceId === 'communications')
        ) {
            return t(`hardwareAccessCheck.deviceLabel.${deviceId}`) + label;
        }

        return label;
    }

    renderDeviceList(deviceList) {
        // React properly if null is given as deviceList.
        if (!deviceList) {
            return null;
        }

        return deviceList.map(({ deviceId, label }) => (
            <option key={deviceId} value={deviceId}>
                {this.improveDeviceLabel(deviceId, label)}
            </option>
        ));
    }

    render() {
        const {
            audioDeviceId,
            audioDevices,
            isConfirmHardwareSettingsButtonDisabled,
            speakerDeviceId,
            speakerDevices,
            showSelectAudioDevice,
            showSelectSpeakerDevice,
            showSelectWebcamDevice,
            showWarningMissingWebcamDevice,
            showWarningMissingAudioDevice,
            videoDevices,
            webcamDeviceId,
            webcamStream,
        } = this.props;
        const isReadOnly = isConfirmHardwareSettingsButtonDisabled;

        const audioOptions = this.renderDeviceList(audioDevices);
        const speakerOptions = this.renderDeviceList(speakerDevices);
        const webcamOptions = this.renderDeviceList(videoDevices);

        return (
            <div className={styles.hardwareSettings}>
                <div className={styles.hardwareSelectorBlock}>
                    { showSelectWebcamDevice &&
                        <IconSelect icon="video-camera">
                            <select
                                id="webcamDeviceSelector"
                                className={styles.hardwareSelector}
                                disabled={isReadOnly}
                                onChange={this.handleSwitchWebcamDevice}
                                value={webcamDeviceId}
                            >
                                {webcamOptions}
                            </select>
                        </IconSelect>
                    }

                    { showWarningMissingWebcamDevice &&
                        <div className="webcamDeviceWarning">
                            {t('hardwareAccessCheck.missingWebcamDevice')}
                        </div>
                    }

                    { showSelectAudioDevice &&
                        <IconSelect icon="microphone">
                            <select
                                id="audioDeviceSelector"
                                className={styles.hardwareSelector}
                                disabled={isReadOnly}
                                onChange={this.handleSwitchAudioDevice}
                                value={audioDeviceId}
                            >
                                {audioOptions}
                            </select>
                        </IconSelect>
                    }

                    { showWarningMissingAudioDevice &&
                        <div className="audioDeviceWarning">
                            {t('hardwareAccessCheck.missingAudioDevice')}
                        </div>
                    }

                    { showSelectSpeakerDevice &&
                        <IconSelect icon="volume-up">
                            <select
                                id="speakerDeviceSelector"
                                className={styles.hardwareSelector}
                                disabled={isReadOnly}
                                onChange={this.handleSwitchSpeakerDevice}
                                value={
                                    this.speakerSelectValue(
                                        speakerDevices,
                                        speakerDeviceId,
                                    )
                                }
                            >
                                {speakerOptions}
                            </select>
                        </IconSelect>
                    }

                    <div>
                        <PlayTestSoundButtonWithAudio
                            speakerDeviceId={speakerDeviceId}
                        />
                    </div>

                    <button
                        id="confirm-hw-settings-button"
                        className={`${styles.button} btn`}
                        onClick={this.handleConfirmSettings}
                        disabled={isConfirmHardwareSettingsButtonDisabled}
                    >
                        {t('hardwareAccessCheck.doConfirmHardwareSettings')}
                    </button>
                </div>

                <LiveCamera stream={webcamStream} />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return confirmHardwareSettingsSelector.mapStateToProps(state);
}

const ConfirmHardwareSettingsContainer = connect(mapStateToProps)(ConfirmHardwareSettings);
export default ConfirmHardwareSettingsContainer;
